import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Button, Input, Space, Spin, Table, Tag, Typography } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import EntryModal from './EntryModal';
import { getCompassModuleById, getCompassModules } from '../../api/compass_modules';
import { CorsiaLookupRow, CorsiaMethodologyEntry, CorsiaPhase } from '../../api/corsia';

export const DEFAULT_PHASES: CorsiaPhase[] = [
  { key: 'pilot', label: 'Pilot' },
  { key: 'phase_1', label: 'Phase 1' },
  { key: 'phase_2', label: 'Phase 2' },
];

// The platform API returns { response: [...] } — see Projects/edit/projectdetail.
const toArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.response)) return payload.response;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

/** Flatten a TipTap node to its plain text. */
const nodeText = (node: any): string => {
  if (!node) return '';
  if (typeof node.text === 'string') return node.text;
  return (node.content ?? []).map(nodeText).join('');
};

const PHASE_HEADINGS: { key: string; test: RegExp }[] = [
  { key: 'pilot', test: /\bpilot\b/i },
  { key: 'phase_1', test: /\b(first|phase\s*1)\b/i },
  { key: 'phase_2', test: /\b(second|phase\s*2)\b/i },
];

const isHeadingNode = (node: any) =>
  node?.type === 'heading' ||
  (node?.type === 'paragraph' &&
    (node.content ?? []).length > 0 &&
    (node.content ?? []).every((c: any) =>
      (c.marks ?? []).some((m: any) => m.type === 'bold' || m.type === 'strong')
    ));

/**
 * Compass keeps CORSIA eligibility as one document whose phases are marked by
 * headings ("CORSIA Pilot Phase", "CORSIA First Phase", ...). Split on those so
 * each phase gets its own document; a document with no recognisable headings
 * falls back to Pilot rather than being dropped.
 */
export const splitEligibilityByPhase = (doc: any): Record<string, any> => {
  const blocks: any[] = doc?.content ?? [];
  if (!blocks.length) return {};

  const buckets: Record<string, any[]> = {};
  let current: string | null = null;

  blocks.forEach((node) => {
    if (isHeadingNode(node)) {
      const match = PHASE_HEADINGS.find((p) => p.test.test(nodeText(node)));
      if (match) {
        current = match.key;
        buckets[current] = buckets[current] ?? [];
        return; // the heading is the section label, not body copy
      }
    }
    if (current) buckets[current].push(node);
  });

  if (!Object.keys(buckets).length) return { pilot: doc };

  return Object.fromEntries(
    Object.entries(buckets).map(([key, content]) => [key, { type: 'doc', content }])
  );
};

/** Upgrade rows saved before the lookup was grouped by project type. */
const normaliseGroups = (input: any[]): CorsiaLookupRow[] => {
  const groups: CorsiaLookupRow[] = [];
  (input ?? []).forEach((row: any) => {
    if (Array.isArray(row?.methodologies)) {
      groups.push({ ...row, methodologies: row.methodologies });
      return;
    }
    const projectType = row?.project_type ?? '';
    const entry: CorsiaMethodologyEntry = {
      id: row?.id ?? uuidv4(),
      standard: row?.standard ?? '',
      methodology: row?.methodology ?? '',
      versions: row?.versions ?? [],
      phases: row?.phases ?? DEFAULT_PHASES.map((p) => ({ ...p })),
      eligibility: row?.eligibility ?? {},
    };
    const existing = groups.find((g) => g.project_type === projectType);
    if (existing) existing.methodologies.push(entry);
    else groups.push({ id: uuidv4(), project_type: projectType, methodologies: [entry] });
  });
  return groups;
};

type FlatRow = {
  key: string;
  groupId: string | null;
  projectType: string;
  /** Rows sharing a project type collapse into one cell via rowSpan. */
  projectTypeSpan: number;
  /** Same, for a standard repeated within one project type. */
  standardSpan: number;
  entry: CorsiaMethodologyEntry;
  /** True until the row has been saved into the catcher payload. */
  fromCompass: boolean;
};

/**
 * Orders rows so repeated values are adjacent, then measures how many rows each
 * leading cell has to span. Mutates in place and returns the same array.
 */
const applySpans = (list: FlatRow[]) => {
  list.sort(
    (a, b) =>
      a.projectType.localeCompare(b.projectType) ||
      (a.entry.standard ?? '').localeCompare(b.entry.standard ?? '') ||
      (a.entry.methodology ?? '').localeCompare(b.entry.methodology ?? '')
  );
  list.forEach((r) => {
    r.projectTypeSpan = 0;
    r.standardSpan = 0;
  });

  const span = (
    same: (a: FlatRow, b: FlatRow) => boolean,
    assign: (row: FlatRow, size: number) => void
  ) => {
    let i = 0;
    while (i < list.length) {
      let j = i;
      while (j < list.length && same(list[j], list[i])) j += 1;
      assign(list[i], j - i);
      i = j;
    }
  };

  span(
    (a, b) => a.projectType === b.projectType,
    (row, size) => {
      row.projectTypeSpan = size;
    }
  );
  span(
    (a, b) => a.projectType === b.projectType && a.entry.standard === b.entry.standard,
    (row, size) => {
      row.standardSpan = size;
    }
  );
  return list;
};

type Props = {
  rows: CorsiaLookupRow[];
  /** Set once the Compass prefill has been persisted. */
  seededAt?: string;
  onChange: (rows: CorsiaLookupRow[], patch?: Record<string, unknown>) => void;
  readonly?: boolean;
};

const identity = (pt: string, std?: string, meth?: string) =>
  `${pt}|${std ?? ''}|${meth ?? ''}`.toLowerCase();

const entryFromCompass = (c: any, createdAt: string): CorsiaMethodologyEntry => ({
  id: uuidv4(),
  standard: c.standard ?? '',
  methodology: c.methodology ?? '',
  versions: (Array.isArray(c.version) ? c.version : [c.version])
    .filter((v: any) => v != null && String(v).trim() !== '')
    .map((v: any) => String(v).trim()),
  phases: DEFAULT_PHASES.map((p) => ({ ...p })),
  eligibility: splitEligibilityByPhase(c.eligibility),
  created_at: createdAt,
});

const EligibilityLookup: React.FC<Props> = ({
  rows: rawRows,
  seededAt,
  onChange,
  readonly = false,
}) => {
  const rows = useMemo(() => normaliseGroups(rawRows as any[]), [rawRows]);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{
    projectType: string;
    entry: CorsiaMethodologyEntry;
  } | null>(null);

  const { data: compassModules, isLoading: loadingModules } = useQuery(
    'compass-modules',
    getCompassModules
  );

  /** Compass is the prefill source and is only ever read here. */
  const { data: compassCorsiaRows } = useQuery(
    ['compass-corsia-rows', toArray(compassModules).length],
    async () => {
      const modules = toArray(compassModules);
      const details = await Promise.all(
        modules.map((m: any) => getCompassModuleById(m.id).catch(() => null))
      );
      return details.flatMap((d: any, i: number) => {
        const corsia = d?.json_data?.methodologies?.corsia?.data ?? [];
        const projectType = modules[i]?.project_types?.[0]?.project_type?.display_name ?? '';
        return corsia.map((c: any) => ({ ...c, project_type: projectType }));
      });
    },
    { enabled: toArray(compassModules).length > 0 }
  );

  const compassRows = useMemo(() => compassCorsiaRows ?? [], [compassCorsiaRows]);

  /**
   * Compass seeds the table once and is never read again, so what is on screen
   * is always what has been saved.
   */
  const seedRequested = useRef(false);
  useEffect(() => {
    if (readonly || seededAt || seedRequested.current || !compassRows.length) return;
    seedRequested.current = true;

    const seen = new Set(
      rows.flatMap((g) =>
        (g.methodologies ?? []).map((m) => identity(g.project_type, m.standard, m.methodology))
      )
    );
    const seeded = rows.map((g) => ({ ...g, methodologies: [...(g.methodologies ?? [])] }));
    const now = new Date().toISOString();

    compassRows.forEach((c: any) => {
      const projectType = c.project_type ?? '';
      const key = identity(projectType, c.standard, c.methodology);
      if (!projectType || seen.has(key)) return;
      seen.add(key);
      const entry = entryFromCompass(c, now);
      const group = seeded.find((g) => g.project_type === projectType);
      if (group) group.methodologies.push(entry);
      else seeded.push({ id: uuidv4(), project_type: projectType, methodologies: [entry] });
    });

    onChange(seeded, { seeded_at: now });
  }, [readonly, seededAt, compassRows, rows, onChange]);

  const tableRows: FlatRow[] = useMemo(
    () =>
      applySpans(
        rows.flatMap((group) =>
          (group.methodologies ?? []).map((entry) => ({
            key: entry.id,
            groupId: group.id as string | null,
            projectType: group.project_type,
            projectTypeSpan: 0,
            standardSpan: 0,
            entry,
            fromCompass: false,
          }))
        )
      ),
    [rows]
  );

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tableRows;
    const matched = tableRows.filter((r) =>
      [r.projectType, r.entry.standard, r.entry.methodology, ...(r.entry.versions ?? [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
    // Spans are recomputed so merged cells stay contiguous within the filtered set.
    return applySpans(matched);
  }, [tableRows, search]);

  /** The modal commits on Save, for both new and existing entries. */
  const handleSave = useCallback(
    (projectType: string, incoming: CorsiaMethodologyEntry) => {
      const entry = { ...incoming, created_at: incoming.created_at ?? new Date().toISOString() };
      // Drop the entry from wherever it currently lives, then re-home it.
      let next = rows.map((g) => ({
        ...g,
        methodologies: (g.methodologies ?? []).filter((m) => m.id !== entry.id),
      }));
      const target = next.find((g) => g.project_type === projectType);
      next = target
        ? next.map((g) =>
            g.id === target.id ? { ...g, methodologies: [...g.methodologies, entry] } : g
          )
        : [...next, { id: uuidv4(), project_type: projectType, methodologies: [entry] }];

      onChange(next.filter((g) => g.methodologies.length > 0));
      setModalOpen(false);
      setEditing(null);
    },
    [onChange, rows]
  );

  const removeEntry = useCallback(
    (groupId: string, entryId: string) => {
      onChange(
        rows
          .map((g) =>
            g.id === groupId
              ? { ...g, methodologies: g.methodologies.filter((m) => m.id !== entryId) }
              : g
          )
          .filter((g) => g.methodologies.length > 0)
      );
    },
    [onChange, rows]
  );

  if (loadingModules && !seededAt) {
    return (
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  const columns = [
    {
      title: 'Project type',
      dataIndex: 'projectType',
      width: 260,
      onCell: (row: FlatRow) => ({ rowSpan: row.projectTypeSpan }),
      render: (value: string) => <Typography.Text strong>{value || '—'}</Typography.Text>,
    },
    {
      title: 'Registry / Standard',
      width: 140,
      onCell: (row: FlatRow) => ({ rowSpan: row.standardSpan }),
      render: (_: any, row: FlatRow) => row.entry.standard || '—',
    },
    {
      title: 'Methodology',
      render: (_: any, row: FlatRow) => row.entry.methodology || '—',
    },
    {
      title: 'Version(s)',
      width: 200,
      render: (_: any, row: FlatRow) => {
        const versions = (row.entry.versions ?? []).filter((v) => String(v).trim() !== '');
        return versions.length ? (
          <Space size={4} wrap>
            {versions.map((v) => (
              <Tag key={v} color="blue">
                {v}
              </Tag>
            ))}
          </Space>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Eligibility',
      width: 220,
      render: (_: any, row: FlatRow) => {
        const phases = row.entry.phases?.length ? row.entry.phases : DEFAULT_PHASES;
        return (
          <Space size={4} wrap>
            {phases.map((p) => {
              const filled = Boolean(row.entry.eligibility?.[p.key]?.content?.length);
              return (
                <Tag key={p.key} color={filled ? 'green' : 'default'}>
                  {p.label}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <Input.Search
          allowClear
          placeholder="Search project type, standard, methodology or version"
          style={{ maxWidth: 460 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          type="primary"
          disabled={readonly}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          Add entry
        </Button>
      </div>

      <Table
        rowKey="key"
        columns={columns as any}
        dataSource={visibleRows}
        pagination={false}
        bordered
        size="middle"
        locale={{ emptyText: 'No entries yet — add one to pull eligibility from Compass.' }}
        onRow={(row: FlatRow) => ({
          style: { cursor: 'pointer' },
          onClick: () => {
            setEditing({ projectType: row.projectType, entry: row.entry });
            setModalOpen(true);
          },
        })}
      />

      <EntryModal
        open={modalOpen}
        initial={editing}
        compassRows={compassRows as any[]}
        defaultPhases={DEFAULT_PHASES}
        splitEligibility={splitEligibilityByPhase}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        onRemove={
          editing && rows.some((g) => g.methodologies.some((m) => m.id === editing.entry.id))
            ? () => {
                const group = rows.find((g) =>
                  g.methodologies.some((m) => m.id === editing.entry.id)
                );
                if (group) removeEntry(group.id, editing.entry.id);
                setModalOpen(false);
                setEditing(null);
              }
            : undefined
        }
      />
    </div>
  );
};

export default EligibilityLookup;
